// A2F-764: Section 6 body — Save & install.
//
// Replaces v1's SaveInstallSection layout with the v2 design (charcoal
// "YOU'VE BUILT" panel + two big plan cards + email/password +
// full-width orange CTA). Behavior matches v1 exactly: same useSignup
// hook, same email/password/terms validation, same navigation to
// /check-email on success. Terms acceptance stays a required checkbox
// (v1 contract) — A2F-891 moved it above the CTA so the consent ask
// reads in the same beat as the action it gates.
//
// CTA copy varies by tier:
//   trial → "Start 14-day trial & install"
//   free  → "Create Free account & install"
//
// The four-metric YOU'VE BUILT panel (ribbon buttons · packs · rules
// · table presets) is distinct from the section-card status pill,
// which uses three metrics — the panel is the in-body summary the
// user reads at the moment of decision; the pill is the always-on
// counter visible while the section is collapsed.

import { useEffect, useState, type FormEvent } from 'react'
import type { TierChoice } from '../../components/SignupModal'
import type { Profile } from '../../types'
import { useSignup } from '../../lib/useSignup'

interface SummaryCounts {
  ribbonButtons: number
  packs: number
  customRules: number
  tablePresets: number
}

interface SectionSaveInstallProps {
  profile: Profile
  tier: TierChoice
  onTierChange: (next: TierChoice) => void
  summary: SummaryCounts
  onSent: (email: string, tier: TierChoice) => void
}

export function SectionSaveInstall({
  profile,
  tier,
  onTierChange,
  summary,
  onSent,
}: SectionSaveInstallProps) {
  const { phase, error, submit } = useSignup()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [terms, setTerms] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    await submit({ email, password, tier, terms, profile })
  }

  // Surface 'sent' to the parent. tier/email omitted from deps for the
  // same reason as v1 SaveInstallSection — the fieldset is disabled
  // mid-submit so captured values can't drift.
  useEffect(() => {
    if (phase === 'sent') onSent(email, tier)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  if (phase === 'sent') return null

  // PlanToggle's 'free'|'trial' narrowing — the parent's TierChoice
  // includes 'subscribe' which this surface doesn't expose.
  const toggleTier: 'trial' | 'free' = tier === 'free' ? 'free' : 'trial'

  const submitDisabled =
    phase === 'submitting' || !email || password.length < 8 || !terms

  const ctaCopy =
    phase === 'submitting'
      ? 'Creating account…'
      : toggleTier === 'trial'
        ? 'Start 14-day trial & install'
        : 'Create Free account & install'

  return (
    <div className="space-y-5">
      {/* "YOU'VE BUILT" charcoal panel — 4 metrics */}
      <div className="rounded-lg bg-brand-charcoal text-white px-5 py-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-orange mb-2">
          You've built
        </div>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <Metric n={summary.ribbonButtons} unit={summary.ribbonButtons === 1 ? 'ribbon button' : 'ribbon buttons'} />
          <Metric n={summary.packs} unit={summary.packs === 1 ? 'pack' : 'packs'} />
          <Metric n={summary.customRules} unit={summary.customRules === 1 ? 'custom rule' : 'custom rules'} />
          <Metric n={summary.tablePresets} unit={summary.tablePresets === 1 ? 'table preset' : 'table presets'} />
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-5">
        <fieldset disabled={phase === 'submitting'} className="space-y-5 disabled:opacity-60">
          {/* Plan picker — two cards, click to select */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PlanCard
              recommended
              selected={toggleTier === 'trial'}
              onSelect={() => onTierChange('trial')}
              title="14-day trial"
              subtitle="Full Pro · no card required"
              bullets={[
                'All formatting + table presets',
                'All regulatory packs',
                'Unlimited custom rules',
                '$99/year after trial',
              ]}
            />
            <PlanCard
              selected={toggleTier === 'free'}
              onSelect={() => onTierChange('free')}
              title="Stay on Free"
              subtitle="Formatting forever"
              bullets={[
                'Formatting + IND headings',
                'One table preset',
                'Pro features locked',
                'Upgrade anytime',
              ]}
            />
          </div>

          {/* Email + password */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                Email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@lab.com"
                autoComplete="email"
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 disabled:cursor-not-allowed"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                Password
              </span>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 disabled:cursor-not-allowed"
              />
            </label>
          </div>
        </fieldset>

        {/* A2F-891: Terms acceptance precedes the CTA — required for
            submit (v1 contract), still visually subordinate to the
            orange button via small checkbox + muted label. */}
        <label className="flex items-start gap-2 text-[11px] text-slate-500">
          <input
            type="checkbox"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            I accept the{' '}
            <a
              href="https://chronabio.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-brand-charcoal"
            >
              terms of service
            </a>{' '}
            and{' '}
            <a
              href="https://chronabio.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-brand-charcoal"
            >
              privacy policy
            </a>
            .
          </span>
        </label>

        {error ? <p className="text-xs text-red-600">{error}</p> : null}

        <button
          type="submit"
          disabled={submitDisabled}
          className="w-full rounded-lg bg-brand-orange px-5 py-3.5 text-sm font-semibold text-white hover:bg-brand-orange/90 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {ctaCopy}
        </button>

        <p className="text-[11px] text-slate-500 text-center">
          Installs from{' '}
          <strong className="font-semibold text-brand-charcoal">Microsoft AppSource</strong>{' '}
          on your next click. Works in Word for Windows, Mac, and Web.
        </p>
      </form>
    </div>
  )
}

function Metric({ n, unit }: { n: number; unit: string }) {
  return (
    <span className="leading-tight">
      <span className="text-[22px] font-semibold tabular-nums">{n}</span>{' '}
      <span className="text-[12px] text-white/80">{unit}</span>
    </span>
  )
}

interface PlanCardProps {
  recommended?: boolean
  selected: boolean
  title: string
  subtitle: string
  bullets: string[]
  onSelect: () => void
}

function PlanCard({
  recommended,
  selected,
  title,
  subtitle,
  bullets,
  onSelect,
}: PlanCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`relative text-left rounded-xl px-4 pt-5 pb-4 transition-colors ${
        selected
          ? 'bg-brand-orange/5 border-2 border-brand-orange'
          : 'bg-white border-2 border-slate-200 hover:border-slate-300'
      }`}
    >
      {recommended ? (
        <span className="absolute -top-2.5 left-4 inline-block rounded-full bg-brand-orange text-white text-[10px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5">
          Recommended
        </span>
      ) : null}
      <div className="text-base font-semibold text-brand-charcoal">{title}</div>
      <div className="text-[12px] text-slate-500 mt-0.5">{subtitle}</div>
      <ul className="mt-3 space-y-1 text-[12px] text-brand-charcoal/90">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span aria-hidden className="mt-1.5 h-1 w-1 rounded-full bg-brand-charcoal/60 shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </button>
  )
}
